import {
    $createTextNode,
    $getRoot,
    $getSelection,
    $isRangeSelection,
    $isTextNode,
    ElementNode,
    LexicalNode,
    PointType,
} from 'lexical'
import { BaseNode } from '../nodes/BaseNode'
import { TNode, nodeRegistry } from '../nodes/NodeCore'
import { NodeType } from '../nodes/NodeType'
import { SerializedContent } from './JsonUtils'
import { isZeroWidthOrEmpty } from './StringUtils'

export const getCurrentNode = (): LexicalNode | null => {
    let result: LexicalNode | null = null
    const anchorNode = getAnchorNode()
    if (!anchorNode) return null
    const topLevelNode = anchorNode.getTopLevelElement()
    result = topLevelNode as LexicalNode
    //TODO : check `$isNodeSelection` and its application
    //Do we need it here ??
    return result
}

export const getAnchorNode = (): LexicalNode | null => {
    let result: LexicalNode | null = null
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) return null
    const anchorNode = selection?.anchor.getNode()
    result = anchorNode
    return result
}

export const getPointType = (): PointType | null => {
    const selection = $getSelection()
    if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null
    return selection?.anchor
}

export const isCurrentNodeEmpty = (): boolean => {
    let isEmpty = true
    const anchorNode = getAnchorNode()
    if (!anchorNode) return false
    const text = anchorNode.getTextContent().trim()
    return isZeroWidthOrEmpty(text)
    isEmpty = text === '' || text === '\u200B'
    return isEmpty
}

export const hasTextVisibleTextAfterCaret = (currentNode: BaseNode): boolean => {
    const anchor = getPointType()
    const node = anchor?.getNode()
    if (!anchor) return false
    if (!$isTextNode(node)) return false
    //TODO refactor after fixing the zero with character in node insert
    if (currentNode.getFirstChild() == node && node.getNextSibling()) {
        return !isZeroWidthOrEmpty(node.getNextSibling()?.getTextContent())
    }

    const text = anchor!.getNode()!.getTextContent()
    const hasTextAfter =
        anchor.offset < text.length - 1 && text.slice(anchor.offset).trim().length > 0
    console.log('Text after caret in this node?', hasTextAfter, anchor.offset, text)
    return hasTextAfter
}

type InsertNodeProps = {
    type: NodeType
    refNode?: LexicalNode
    content?: string
    strategy?: 'alwaysReplace' | 'alwaysInsert' | 'default'
}
export const $insertNode1 = ({ type, refNode, strategy = 'default', content }: InsertNodeProps) => {
    const node: TNode = nodeRegistry[type]
    if (!node) {
        console.warn(`No node class found for type: ${type}`)
        return
    }
    const newNode = BaseNode.create(node.constructor, content)
    insertOrReplaceNode1({ newNode, refNode, strategy })
    newNode.selectEnd()
    return newNode
}

type InsertOrReplaceNodeOptions1 = Omit<InsertNodeProps, 'type'> & {
    newNode: BaseNode
}
const insertOrReplaceNode1 = ({ newNode, refNode, strategy }: InsertOrReplaceNodeOptions1) => {
    const _currentNode = refNode ?? getCurrentNode()
    if (!_currentNode) {
        console.warn('No top-level node found')
        return
    }
    console.log('strategy', strategy)
    if (strategy === 'alwaysInsert') {
        console.log('inserting')
        return insertNodeAfter(_currentNode, newNode)
    }
    if (strategy === 'alwaysReplace') {
        console.log('inserting')
        return replaceNode(_currentNode, newNode)
    }
    console.log(isCurrentNodeEmpty())
    if (isCurrentNodeEmpty() && _currentNode.getType() !== NodeType.PageBreak) {
        console.log('replacing')
        return replaceNode(_currentNode, newNode)
    } else {
        console.log('inserting')
        return insertNodeAfter(_currentNode, newNode)
    }
}

const insertNodeAfter = (node: LexicalNode, newNode: LexicalNode) => {
    return node.insertAfter(newNode)
}
const replaceNode = (node: LexicalNode, replaceWith: ElementNode) => {
    replaceWith.clear()
    replaceWith.append($createTextNode(node.getTextContent()))
    return node.replace(replaceWith)
}

export const insertNodes = (nodes: SerializedContent[], _insertAfter?: BaseNode) => {
    let insertAfter = _insertAfter ?? getCurrentNode()
    if (!insertAfter) return
    console.log('i af ', insertAfter, nodes)
    nodes.forEach((node) => {
        const textContent = node.children.map((child) => child.text).join('')
        const insertedNode = $insertNode1({
            type: node.type as NodeType,
            refNode: insertAfter!,
            strategy: 'alwaysInsert',
            content: textContent,
        })
        if (insertedNode) {
            insertAfter = insertedNode
        }
    })
}
///find the next scene node and insert `[nodes]` before it
///if no next scene is there , insert nodes at the end
export const insertNodesAsNextScene = (nodes: SerializedContent[], _currentNode: BaseNode) => {
    const currentNode = _currentNode ?? getCurrentNode()
    const nextSceneNode = currentNode.getNextSceneNode()
    const insertAfter: BaseNode | null =
        nextSceneNode?.getPreviousSibling() ?? currentNode.getLastNodeInEditor()
    //console.log('insertAfter', nodes)
    if (insertAfter) insertNodes(nodes, insertAfter)
}
export const replaceCurrentScene = (nodes: SerializedContent[], _currentNode: BaseNode) => {
    const currentNode = _currentNode ?? getCurrentNode()
    const nextSceneNode = currentNode.getNextSceneNode()
    let walker = nextSceneNode?.getPreviousSibling() ?? $getRoot().getLastChild()
    while (walker) {
        const current = walker
        walker = walker.getPreviousSibling()
        current.remove()

        if (current.getType() === 'scene') {
            break // remove and stop
        }
    }

    if (walker) {
        return insertNodes(nodes, walker as BaseNode)
    }
    const [firstNode, ...remainingNodes] = nodes

    const node: TNode = nodeRegistry[firstNode.type as NodeType]
    if (!node) {
        return
    }
    const textContent = firstNode.children.map((child) => child.text).join('')
    const tempFirstNode = BaseNode.create(node.constructor, textContent)
    nextSceneNode?.insertBefore(tempFirstNode)
    insertNodes(remainingNodes, tempFirstNode)
}
const insertPageBreak = () => {
    const pageBreakClass = nodeRegistry[NodeType.PageBreak]
    const newNode = BaseNode.create(pageBreakClass.constructor)
    // insertOrReplaceNode({ newNode, alwaysReplace: false })
    insertOrReplaceNode1({ newNode: newNode })
    // insertNode(NodeType.Scene, newNode)
    $insertNode1({ type: NodeType.Scene, refNode: newNode })
}

export const insertPageBreakOrNode = (type: NodeType) => {
    if (type === NodeType.PageBreak) {
        insertPageBreak()
    } else {
        //insertNode(type as NodeType)
        $insertNode1({ type: type as NodeType, strategy: 'alwaysReplace' })
    }
}
export { isZeroWidthOrEmpty }
